import Tabs from '@src/components/common/Tabs';
import { MODAL_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import CommentsIcon from '@src/svgs/CommentsIcon';
import DownloadIcon from '@src/svgs/DownloadIcon';
import GlobeIcon from '@src/svgs/GlobeIcon';
import PostsIcon from '@src/svgs/PostsIcon';
import ReelIcon from '@src/svgs/ReelIcon';
import TemplatesIcon from '@src/svgs/TemplatesIcon';
import TranscriptIcon from '@src/svgs/TranscriptIcon';
import React from 'react';
import styled from 'styled-components';

import type { Tab } from '@src/components/common/Tabs';

export type ShareModalTab =
  | 'templates'
  | 'posts'
  | 'transcript'
  | 'languages'
  | 'reelEditor'
  | 'comments'
  | 'downloads';

// Keyed by the union so adding a `ShareModalTab` fails to compile until it is
// listed here — otherwise a new tab silently loses `?clipTab` support.
const SHARE_MODAL_TABS: Record<ShareModalTab, true> = {
  templates: true,
  posts: true,
  transcript: true,
  languages: true,
  reelEditor: true,
  comments: true,
  downloads: true,
};

/** Validate a raw `?clipTab=` URL value into a `ShareModalTab` (else undefined). */
export const parseClipTab = (
  value: string | null,
): ShareModalTab | undefined =>
  value && Object.hasOwn(SHARE_MODAL_TABS, value)
    ? (value as ShareModalTab)
    : undefined;

/** What gates a subtab's content in the clip preview. */
export interface ShareModalTabFlags {
  enableShare: boolean;
  enableTranscript: boolean;
  enableComments: boolean;
  enableReelEditor: boolean;
  /**
   * PLAT-11035 — a translation reviewer gets the Transcript tab and nothing
   * else. Folded in here rather than applied to the tab row's props directly, so
   * the `?clipTab` reconcile sees the same hidden set the row does.
   */
  reviewerMode?: boolean;
}

export interface ShareModalTabVisibility {
  hidden: Set<ShareModalTab>;
  /** The first tab this configuration leaves visible. */
  defaultTab: ShareModalTab;
}

/**
 * Preference order for `defaultTab` — first one still visible wins. Deliberately
 * without `reelEditor`: it is the opening tab for a *reel* specifically
 * (`initialShareModalTab`), not a fallback a plain clip should ever land on.
 */
const DEFAULT_TAB_ORDER: ReadonlyArray<ShareModalTab> = [
  'transcript',
  'templates',
  'downloads',
  'comments',
  'posts',
];

/**
 * Which subtabs the current flags hide, plus the fallback. One source for both
 * the tab-row's `hide*` props and the `?clipTab` reconcile, so the two can't
 * disagree about what's visible (PLAT-10948).
 */
export function shareModalTabVisibility({
  enableShare,
  enableTranscript,
  enableComments,
  enableReelEditor,
  reviewerMode = false,
}: ShareModalTabFlags): ShareModalTabVisibility {
  const hidden = new Set<ShareModalTab>();
  if (!enableShare || reviewerMode) hidden.add('posts');
  if (!enableTranscript) hidden.add('transcript');
  // PLAT-11877 — the transcript's languages, a place of their own beside it.
  // Not for a translation reviewer, whose one tab is Transcript (PLAT-11035):
  // the transcript's globe opens the languages over it instead.
  if (!enableTranscript || reviewerMode) hidden.add('languages');
  if (!enableComments || reviewerMode) hidden.add('comments');
  // Restructuring a reel is authoring, not reviewing — a translation reviewer
  // gets the Transcript tab and nothing else (PLAT-11035).
  if (!enableReelEditor || reviewerMode) hidden.add('reelEditor');
  if (reviewerMode) {
    hidden.add('templates');
    hidden.add('downloads');
  }
  // No tab is unconditionally visible any more — reviewer mode gates templates
  // and downloads, which used to be the guaranteed fallbacks — so search the
  // order instead of assuming. The `??` is unreachable in practice (reviewer
  // mode never hides transcript) and only keeps the return type honest.
  const defaultTab =
    DEFAULT_TAB_ORDER.find(tab => !hidden.has(tab)) ?? 'transcript';
  return { hidden, defaultTab };
}

/**
 * The tab to actually show: the requested one when the flags allow it, else the
 * default. A `?clipTab` naming a flag-hidden tab would otherwise render an empty
 * pane — the content is flag-gated while `Tabs` honors any controlled id.
 */
export function resolveVisibleClipTab(
  tab: ShareModalTab,
  { hidden, defaultTab }: ShareModalTabVisibility,
): ShareModalTab {
  return hidden.has(tab) ? defaultTab : tab;
}

/**
 * Which tab the clip preview opens on, in precedence order: a `?clipTab` from a
 * shared or refreshed link (PLAT-10948), then a one-shot request from the card
 * (the comment badge), then a reel's own structure (PLAT-11051), then the
 * default. The winner goes through `resolveVisibleClipTab` so the seed can never
 * land on a tab the row won't render.
 */
export function initialShareModalTab({
  fromUrl,
  requestedTab,
  isReel,
  visibility,
}: {
  fromUrl: ShareModalTab | undefined;
  requestedTab: 'comments' | null;
  isReel: boolean;
  visibility: ShareModalTabVisibility;
}): ShareModalTab {
  const requested =
    fromUrl ??
    (requestedTab === 'comments'
      ? 'comments'
      : isReel
        ? 'reelEditor'
        : visibility.defaultTab);
  return resolveVisibleClipTab(requested, visibility);
}

interface ShareModalTabsProps {
  children: (activeTab: ShareModalTab) => React.ReactNode;
  activeTab: ShareModalTab;
  setActiveTab: (tab: ShareModalTab) => void;
  hidePosts?: boolean;
  hideTranscript?: boolean;
  hideLanguages?: boolean;
  hideReelEditor?: boolean;
  hideComments?: boolean;
  hideTemplates?: boolean;
  hideDownloads?: boolean;
  /** Action pinned to the right of the tab row (e.g. Publish and Share). */
  headerAction?: React.ReactNode;
}

export const ShareModalTabs: React.FC<ShareModalTabsProps> = ({
  children,
  activeTab,
  setActiveTab,
  hidePosts,
  hideTranscript,
  hideLanguages,
  hideReelEditor,
  hideComments,
  hideTemplates,
  hideDownloads,
  headerAction,
}) => {
  const tabs: Tab<ShareModalTab>[] = [];
  if (!hideTranscript) {
    tabs.push({
      id: 'transcript',
      label: 'Transcript',
      icon: <TranscriptIcon />,
      testId: 'clip-preview-transcript-tab',
    });
  }
  // PLAT-11877 — the transcript's languages, next to it: which language the
  // transcript is read in, each language's review and dub, adding one. The
  // transcript toolbar's globe opens this tab.
  if (!hideLanguages) {
    tabs.push({
      id: 'languages',
      label: 'Languages',
      icon: <GlobeIcon width="16" height="16" />,
      testId: 'clip-preview-languages-tab',
    });
  }
  // Directly after Transcript: the two are views of the same clip, so
  // structural and word-level work stay one keystroke apart (DE4).
  if (!hideReelEditor) {
    tabs.push({
      id: 'reelEditor',
      label: 'Reel Editor',
      icon: <ReelIcon />,
      testId: 'clip-preview-reel-editor-tab',
    });
  }
  if (!hideTemplates) {
    tabs.push({
      id: 'templates',
      label: 'Templates',
      icon: <TemplatesIcon />,
      testId: 'clip-preview-templates-tab',
    });
  }
  if (!hideComments) {
    tabs.push({
      id: 'comments',
      label: 'Comments',
      icon: <CommentsIcon />,
      testId: 'clip-preview-comments-tab',
    });
  }
  if (!hidePosts) {
    tabs.push({
      id: 'posts',
      label: 'Posts',
      icon: <PostsIcon />,
      testId: 'clip-preview-posts-tab',
    });
  }
  if (!hideDownloads) {
    tabs.push({
      id: 'downloads',
      label: 'Downloads',
      icon: <DownloadIcon width="16" height="16" strokeColor="currentColor" />,
      testId: 'clip-preview-downloads-tab',
    });
  }

  return (
    <Container>
      <Tabs<ShareModalTab>
        variant="icon-only"
        tabs={tabs}
        activeTab={activeTab}
        onChange={setActiveTab}
        listTestId="share-modal-tabs"
        tooltipStyle={MODAL_TOOLTIP_STYLE}
        headerRight={headerAction}
      >
        {tab => children(tab)}
      </Tabs>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  min-height: 0;
  /* Aligns the tab row's bottom border exactly with the left pane's divider.
     The left side (PLAT-11213 title + PLAT-11246 re-measure): LeftPane
     margin-top 24 + title row 30 (18px title on a 22px line, plus the rename
     field's 3px heading padding and 1px border top and bottom, PLAT-11503;
     the type badge is height-locked at 22px so it never sets the row) +
     subtitle 19.4 (4px
     margin + 11px @ 1.4 = 15.4px line) + gap 15 = 88.4. Tab row bottom =
     52.4 + TabHeader 36 = 88.4. */
  padding: 52.4px 0 24px;
  overflow: hidden;
`;
